/**
 * Berechnet die Top-3-Konkurrenten basierend auf Rankings über alle Standorte
 * 
 * @param {Array} locationRanks - Array von Standorten mit companies
 * @param {string} ownCompanyPlaceId - PlaceID des eigenen Unternehmens (wird ausgeschlossen)
 * @returns {Array} Top-3-Konkurrenten mit name, totalPoints, avgRank, locationCount
 */
export const calculateTopCompetitors = (locationRanks, ownCompanyPlaceId) => {
  // Validierung der Eingabedaten
  if (!locationRanks || !Array.isArray(locationRanks) || locationRanks.length === 0) {
    console.log('⚠️ calculateTopCompetitors: Keine locationRanks verfügbar');
    return [];
  }

  console.log('🔍 Berechne Top Konkurrenten für', locationRanks.length, 'Standorte');
  
  // Map für Konkurrenten: placeId -> { name, totalPoints, ranks, locationCount }
  const competitorMap = new Map();

  // Iteriere über alle Standorte
  locationRanks.forEach((location, locationIndex) => {
    if (!location.companies || !Array.isArray(location.companies)) {
      console.log(`⚠️ Standort ${locationIndex} hat keine companies`);
      return;
    }

    // Iteriere über alle Firmen an diesem Standort
    location.companies.forEach((company) => {
      // Eigenes Unternehmen ausschließen
      if (company.placeId === ownCompanyPlaceId) {
        return;
      }

      // Nur Firmen mit gültigen Rängen (1-10) berücksichtigen
      if (!company.rank || company.rank < 1 || company.rank > 10) {
        return;
      }

      // Punkte berechnen: Platz 1 = 10 Punkte, Platz 2 = 9 Punkte, ..., Platz 10 = 1 Punkt
      const points = 11 - company.rank;

      // Eindeutigen Schlüssel für Unternehmen erstellen (placeId oder Name als Fallback)
      const key = company.placeId || company.name;
      
      if (!key) {
        console.log('⚠️ Firma ohne placeId und Name gefunden:', company);
        return;
      }

      // Competitor-Daten sammeln oder aktualisieren
      if (competitorMap.has(key)) {
        const competitor = competitorMap.get(key);
        competitor.totalPoints += points;
        competitor.ranks.push(company.rank);
        competitor.locationCount++;
      } else {
        competitorMap.set(key, {
          name: company.name || 'Unbekanntes Unternehmen',
          placeId: company.placeId || null,
          totalPoints: points,
          ranks: [company.rank],
          locationCount: 1
        });
      }
    });
  });

  // Map zu Array konvertieren und Durchschnittswerte berechnen
  const competitors = Array.from(competitorMap.values()).map(competitor => ({
    ...competitor,
    avgRank: competitor.ranks.reduce((sum, rank) => sum + rank, 0) / competitor.ranks.length
  }));

  // Nach Gesamtpunkten sortieren (höchste zuerst)
  competitors.sort((a, b) => b.totalPoints - a.totalPoints);

  // Debug-Ausgabe
  console.log('📊 Konkurrenten-Analyse Ergebnis:');
  console.log(`   Gesamt gefunden: ${competitors.length} Konkurrenten`);
  if (competitors.length > 0) {
    console.log('   Top 3:');
    competitors.slice(0, 3).forEach((comp, index) => {
      console.log(`   ${index + 1}. ${comp.name}: ${comp.totalPoints} Punkte (Ø Rang ${comp.avgRank.toFixed(1)}, ${comp.locationCount} Standorte)`);
    });
  }

  // Top-3 zurückgeben
  return competitors.slice(0, 3);
};

/**
 * Hilfsfunktion um Konkurrenten-Statistiken zu loggen (für Debugging)
 * @param {Array} competitors - Array der berechneten Konkurrenten
 * @param {string} keyword - Das analysierte Keyword
 */
export const logCompetitorStats = (competitors, keyword) => {
  console.group(`🏆 Top Konkurrenten für "${keyword}"`);
  
  if (competitors.length === 0) {
    console.log('Keine Konkurrenten gefunden');
  } else {
    competitors.forEach((competitor, index) => {
      console.log(`${index + 1}. ${competitor.name}`);
      console.log(`   Punkte: ${competitor.totalPoints}`);
      console.log(`   Durchschnittlicher Rang: ${competitor.avgRank.toFixed(1)}`);
      console.log(`   Präsent an ${competitor.locationCount} Standorten`);
      console.log('---');
    });
  }
  
  console.groupEnd();
};